import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Hotel } from '../../components/HotelBox/HotelBox';
import { AgentAdditionalInfo } from '../ShoppingCart/agentActions/AgentActions';

// New interfaces for the hotel object structure
export interface HotelLowestRoomRate {
  total_price: number;
  currency: string;
  is_free_cancellation_available: boolean;
  board: string;
  room_name: string;
}

export interface HotelInfo {
  name: string;
  stars: number;
  regionId: number;
  s3Path: string;
  images: string[];
  languages: string;
  address: string;
  countryCode: string;
  type: string;
  longitude: number;
  latitude: number;
  dateUpdated: string;
  views: number;
}

export interface HotelData {
  identifier: string;
  lowest_room_rate: HotelLowestRoomRate;
  info: HotelInfo;
  is_free_cancellation_available: boolean;
  agent_additional_info?: AgentAdditionalInfo;
}
export interface HotelSearchState {
  // Hotel search results from API
  allHotels: HotelData[];
  filteredHotels: HotelData[];

  // Pagination state
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalHotels: number;

  // Search and filter states
  hotelDates: string;
  isLoading: boolean;
  error: string | null;

  // UI states
  selectedHotelId: string | null;
  viewMode: 'grid' | 'list';
}

const ITEMS_PER_PAGE = 30;

const initialState: HotelSearchState = {
  allHotels: [],
  filteredHotels: [],
  currentPage: 1,
  totalPages: 0,
  itemsPerPage: ITEMS_PER_PAGE,
  totalHotels: 0,
  hotelDates: '',
  isLoading: false,
  error: null,
  selectedHotelId: null,
  viewMode: 'grid',
};

export const hotelSlice = createSlice({
  name: 'hotels',
  initialState,
  reducers: {
    // Set all hotels from API response
    setHotels: (state, action: PayloadAction<HotelData[]>) => {
      state.allHotels = action.payload;
      state.filteredHotels = action.payload;
      state.totalHotels = action.payload?.length;
      state.totalPages = Math.ceil(action.payload?.length / state.itemsPerPage);
      state.currentPage = 1; // Reset to first page when new data arrives
      state.error = null;
    },

    // Set filtered hotels (after client-side filtering)
    setFilteredHotels: (state, action: PayloadAction<HotelData[]>) => {
      state.filteredHotels = action.payload;
      state.totalHotels = action.payload?.length;
      state.totalPages = Math.ceil(action.payload?.length / state.itemsPerPage);
      state.currentPage = 1; // Reset to first page when filters change
    },

    // Update hotel dates
    updateHotelDates: (state, action: PayloadAction<string>) => {
      state.hotelDates = action.payload;
    },

    // Pagination actions
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.totalPages = Math.ceil(state.totalHotels / action.payload);
      state.currentPage = 1; // Reset to first page when changing items per page
    },

    // UI state actions
    setSelectedHotel: (state, action: PayloadAction<string | null>) => {
      state.selectedHotelId = action.payload;
    },

    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },

    // Loading and error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null; // Clear error when starting new request
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Reset all data
    resetHotelState: (state) => {
      return initialState;
    },

    // Add a single hotel (useful for dynamic updates)
    addHotel: (state, action: PayloadAction<HotelData>) => {
      state.allHotels.push(action.payload);
      state.filteredHotels.push(action.payload);
      state.totalHotels = state.filteredHotels?.length;
      state.totalPages = Math.ceil(state.totalHotels / state.itemsPerPage);
    },

    // Update a specific hotel
    updateHotel: (
      state,
      action: PayloadAction<{ identifier: string; hotel: Partial<HotelData> }>
    ) => {
      const { identifier, hotel } = action.payload;

      const allHotelIndex = state.allHotels.findIndex(
        (h) => h.identifier === identifier
      );
      if (allHotelIndex !== -1) {
        state.allHotels[allHotelIndex] = {
          ...state.allHotels[allHotelIndex],
          ...hotel,
        };
      }

      const filteredHotelIndex = state.filteredHotels.findIndex(
        (h) => h.identifier === identifier
      );
      if (filteredHotelIndex !== -1) {
        state.filteredHotels[filteredHotelIndex] = {
          ...state.filteredHotels[filteredHotelIndex],
          ...hotel,
        };
      }
    },

    // Remove a hotel
    removeHotel: (state, action: PayloadAction<string>) => {
      const identifier = action.payload;
      state.allHotels = state.allHotels.filter(
        (h) => h.identifier !== identifier
      );
      state.filteredHotels = state.filteredHotels.filter(
        (h) => h.identifier !== identifier
      );
      state.totalHotels = state.filteredHotels?.length;
      state.totalPages = Math.ceil(state.totalHotels / state.itemsPerPage);

      // Adjust current page if needed
      if (state.currentPage > state.totalPages && state.totalPages > 0) {
        state.currentPage = state.totalPages;
      }
    },
  },
});

export const {
  setHotels,
  setFilteredHotels,
  updateHotelDates,
  setCurrentPage,
  setItemsPerPage,
  setSelectedHotel,
  setViewMode,
  setLoading,
  setError,
  clearError,
  resetHotelState,
  addHotel,
  updateHotel,
  removeHotel,
} = hotelSlice.actions;

// Selectors for easy access to state
export const selectAllHotels = (state: { hotels: HotelSearchState }) =>
  state.hotels.allHotels;
export const selectFilteredHotels = (state: { hotels: HotelSearchState }) =>
  state.hotels.filteredHotels;
export const selectCurrentPage = (state: { hotels: HotelSearchState }) =>
  state.hotels.currentPage;
export const selectTotalPages = (state: { hotels: HotelSearchState }) =>
  state.hotels.totalPages;
export const selectItemsPerPage = (state: { hotels: HotelSearchState }) =>
  state.hotels.itemsPerPage;
export const selectTotalHotels = (state: { hotels: HotelSearchState }) =>
  state.hotels.totalHotels;
export const selectHotelDates = (state: { hotels: HotelSearchState }) =>
  state.hotels.hotelDates;
export const selectIsLoading = (state: { hotels: HotelSearchState }) =>
  state.hotels.isLoading;
export const selectError = (state: { hotels: HotelSearchState }) =>
  state.hotels.error;
export const selectSelectedHotelId = (state: { hotels: HotelSearchState }) =>
  state.hotels.selectedHotelId;
export const selectViewMode = (state: { hotels: HotelSearchState }) =>
  state.hotels.viewMode;

// Computed selectors
export const selectPaginatedHotels = (state: { hotels: HotelSearchState }) => {
  const { filteredHotels, currentPage, itemsPerPage } = state.hotels;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return filteredHotels.slice(startIndex, endIndex);
};

export const selectSelectedHotel = (state: { hotels: HotelSearchState }) => {
  const { filteredHotels, selectedHotelId } = state.hotels;
  return (
    filteredHotels.find((hotel) => hotel.identifier === selectedHotelId) || null
  );
};

export default hotelSlice.reducer;
